import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { LoadingButton, StudyLesson, StudyPlanTopic } from '@lf/ui-primitives';
import AddNewLesson from './AddNewLesson';
import EditLesson from '../../components/EditLesson';
import EditTopic from '../../components/EditTopic';
import { StudyPlanFeedback } from '@lf/ui-primitives';
import { reviseStudyPlanFromFeedback, streamLesson } from '@lf/api-client/lfApiStudyPlan';
import { buildStudyPlanPayload, cloneLessons } from '../../lib/studyPlan/draft';
import { applyLessonReorder, applyTopicDrag } from '../../lib/studyPlan/dragDrop';

const EditStudyPlan = ({ username, lessonPlan, onOk, onCancel }) => {
    const [plan, setPlan] = useState(lessonPlan || {
        subject: '',
        goal: '',
        previous_knowledge: '',
        num_lessons: '5',
        needs: '',
        emoji: '',
    });
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);
    const [lessons1, setLessons1] = useState(() => cloneLessons(lessonPlan ? lessonPlan.lessons : []));
    const [originalLessons] = useState(() => cloneLessons(lessonPlan ? lessonPlan.lessons : []));

    const [isAddLessonDialogOpen, setIsAddLessonDialogOpen] = useState(false);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [selectedLesson, setSelectedLesson] = useState(null); // 'lesson1:0'
    const [selectedTopic, setSelectedTopic] = useState(null); // 'lesson1:0:0'
    const [isEditLessonDialogOpen, setIsEditLessonDialogOpen] = useState(false);
    const [isEditTopicDialogOpen, setIsEditTopicDialogOpen] = useState(false);

    const [draggingDraggableId, setDraggingDraggableId] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [isRegeneratingFromFeedback, setIsRegeneratingFromFeedback] = useState(false);
    const [feedbackWarnings, setFeedbackWarnings] = useState([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPlan(prevState => ({ ...prevState, [name]: value }));
        setIsSaveEnabled(true);
    };

    const onRegenerateFromFeedback = async () => {
        const feedbackText = feedback.trim();
        if (!feedbackText || isRegeneratingFromFeedback) return;

        setIsRegeneratingFromFeedback(true);
        setFeedbackWarnings([]);
        try {
            const response = await reviseStudyPlanFromFeedback({
                child_username: username,
                subject: plan.subject,
                goal: plan.goal,
                previous_knowledge: plan.previous_knowledge,
                needs: plan.needs,
                feedback: feedbackText,
                original_lessons: originalLessons,
                working_lessons: lessons1,
                target_lesson_ids: [],
                llm_name: 'gemini',
            });
            setLessons1(response.lessons || []);
            setIsSaveEnabled(true);
            setSelectedLesson(null);
            setSelectedTopic(null);
            setFeedbackWarnings(response.warnings || []);
        }
        catch (error) {
            console.error('Error revising study plan from feedback:', error);
        }
        finally {
            setIsRegeneratingFromFeedback(false);
        }
    };

    const onDragUpdate = (update) => {
        setDraggingDraggableId(!update.destination ? update.draggableId : '');
    };
    
    const onDragEnd = (result) => {
        setDraggingDraggableId('');

        if (result.type === "lessons")
            onDragLesson(result);
        else
            onDragTopic(result);

        setIsSaveEnabled(true);
    };

    const onDragLesson = (result) => {
        setLessons1(applyLessonReorder(result, lessons1));
    };

    const onDragTopic = (result) => {
        setLessons1(applyTopicDrag(result, lessons1));
    };

    const onLessonClick = (source, lessonIndex, e) => {
        if (!isReorderMode) {
            if (e) e.stopPropagation();

            const newSelectedLesson = `${source}:${lessonIndex}`;
            setSelectedLesson(selectedLesson === newSelectedLesson ? null : newSelectedLesson);
            setSelectedTopic(null);
        }
    };

    const onTopicClick = (source, lessonIndex, topicIndex, e) => {
        if (!isReorderMode) {
            if (e) e.stopPropagation();

            const newSelectedTopic = `${source}:${lessonIndex}:${topicIndex}`;
            setSelectedTopic(selectedTopic === newSelectedTopic ? null : newSelectedTopic);
            setSelectedLesson(null);
        }
    };

    const onLessonEdit = (source, lessonIndex, e) => {
        if (e) e.stopPropagation();
        setIsEditLessonDialogOpen(true);
    };

    const onLessonDelete = (source, lessonIndex, e) => {
        if (e) e.stopPropagation();
        const { lessons, setLessons } = { lessons: lessons1, setLessons: setLessons1 };
        
        const newLessons = [...lessons];
        newLessons.splice(lessonIndex, 1);
        setLessons(newLessons);
        setSelectedLesson(null);
        setIsSaveEnabled(true);
    };

    const onTopicEdit = (source, lessonIndex, topicIndex, e) => {
        if (e) e.stopPropagation();
        setIsEditTopicDialogOpen(true);
    };

    const onTopicDelete = (source, lessonIndex, topicIndex, e) => {
        if (e) e.stopPropagation();
        const { lessons, setLessons } = { lessons: lessons1, setLessons: setLessons1 };
        
        const newLessons = [...lessons];
        newLessons[lessonIndex].topics.splice(topicIndex, 1);
        setLessons(newLessons);
        setSelectedTopic(null);
        setIsSaveEnabled(true);
    };

    const handleAddLesson = () => {
        setIsAddLessonDialogOpen(true);
    };

    const handleAddNewLesson = async (lessonGoals) => {
        await streamLesson(plan, lessonGoals, username, lessons1, setLessons1);
        setIsSaveEnabled(true);
    };

    const toggleReorderMode = () => {
        setIsReorderMode(!isReorderMode);
        // Clear selections when toggling reorder mode
        setSelectedLesson(null);
        setSelectedTopic(null);
    };

    const handleEditLesson = (editedLesson) => {
        if (selectedLesson) {
            const [, lessonIndex] = selectedLesson.split(':');
            const { lessons, setLessons } = { lessons: lessons1, setLessons: setLessons1 };
            
            const newLessons = [...lessons];
            newLessons[lessonIndex] = { ...newLessons[lessonIndex], ...editedLesson };
            setLessons(newLessons);
            setIsSaveEnabled(true);
        }

        setIsEditLessonDialogOpen(false);
    };

    const handleEditTopic = (editedTopic) => {
        if (selectedTopic) {
            const [, lessonIndex, topicIndex] = selectedTopic.split(':');
            const { lessons, setLessons } = { lessons: lessons1, setLessons: setLessons1 };
            
            const newLessons = [...lessons];
            newLessons[lessonIndex].topics[topicIndex] = { ...newLessons[lessonIndex].topics[topicIndex], ...editedTopic };
            setLessons(newLessons);
            setIsSaveEnabled(true);
        }
        setIsEditTopicDialogOpen(false);
    };

    const getSelectedLesson = () => {
        if (!selectedLesson) return null;
        
        const [, lessonIndex] = selectedLesson.split(':');
        const lessons = lessons1;
        return lessons[lessonIndex];
    };

    const getSelectedTopic = () => {
        if (!selectedTopic) return null;
        
        const [, lessonIndex, topicIndex] = selectedTopic.split(':');
        const lessons = lessons1;
        return lessons[lessonIndex].topics[topicIndex];
    };

    const isTopicSelected = (source, lessonIndex, topicIndex) => {
        return selectedTopic === `${source}:${lessonIndex}:${topicIndex}`;
    };

    const renderTopic = (source, lessonIndex, topicIndex, topic, provided) => {
        return (
            <div key={`${source}:${topic.id}`}>
                <Draggable draggableId={`${source}:${lessonIndex}:${topicIndex}`} 
                        key={`${lessonIndex}:${topicIndex}`} 
                        index={topicIndex}
                        isDragDisabled={!isReorderMode}>
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps}
                            className={`${`${source}:${lessonIndex}:${topicIndex}` === draggingDraggableId ? 'opacity-50' : 'opacity-100'} relative`}
                            onClick={(e) => onTopicClick(source, lessonIndex, topicIndex, e)}>
                            <div className={`flex items-center relative ${
                                    isTopicSelected(source, lessonIndex, topicIndex) ? 'pr-20' : ''
                                }`}>
                                <StudyPlanTopic 
                                    topic={topic} 
                                    index={topicIndex}
                                    topicType={selectedTopic === `${source}:${lessonIndex}:${topicIndex}` ? '' : 'current'}
                                />
                                <div className="flex-grow"></div>
                                
                                {selectedTopic === `${source}:${lessonIndex}:${topicIndex}` && !isReorderMode && (
                                    <div className="absolute top-0 right-2 flex space-x-1 ml-2">
                                        <LoadingButton 
                                            imgSrc="/icon-edit.png" 
                                            text="Edit"
                                            bgColor="white"
                                            onClick={(e) => onTopicEdit(source, lessonIndex, topicIndex, e)}
                                        />
                                        <LoadingButton 
                                            imgSrc="/icon-trash.png" 
                                            text="Delete"
                                            bgColor="white"
                                            onClick={(e) => onTopicDelete(source, lessonIndex, topicIndex, e)}
                                        />
                                    </div>
                                )}
                                
                                {isReorderMode && (
                                    <div 
                                        {...provided.dragHandleProps}
                                        className="flex items-center justify-center cursor-grab ml-2"
                                    >
                                        <span className="text-2xl text-gray-500 px-2">≡</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Draggable>
            </div>
        );
    };

    const renderLesson = (source, lesson, lessonIndex, lessons) => {
        return (
            <Draggable key={`${source}:${lessonIndex}`} draggableId={`${source}:${lessonIndex}`} index={lessonIndex} isDragDisabled={!isReorderMode}>
                {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}
                        className={`${`${source}:${lessonIndex}` === draggingDraggableId ? 'opacity-50' : 'opacity-100'} relative ${isReorderMode ? '' : 'cursor-pointer'}`}
                        onClick={(e) => onLessonClick(source, lessonIndex, e)}>
                        <StudyLesson 
                            lesson={lesson} 
                            index={lessonIndex} 
                            isLastLesson={lessonIndex === lessons.length - 1}
                            lessonBoxStyle={selectedLesson === `${source}:${lessonIndex}` ? 'bg-blue-500 text-white border-blue-500' : 'bg-white'}
                            isReorderMode={isReorderMode}
                        >
                            <Droppable key={`${source}:${lessonIndex}`} droppableId={`${source}:${lessonIndex}`} type="topics">
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                {
                                    lesson.topics && lesson.topics.map((topic, topicIndex) => 
                                        renderTopic(source, lessonIndex, topicIndex, topic, provided)
                                    )
                                }
                                {provided.placeholder}
                                </div>
                            )}
                            </Droppable>
                        </StudyLesson>                                                        
                        {selectedLesson === `${source}:${lessonIndex}` && !isReorderMode && (
                            <div className="absolute top-2 right-2 flex space-x-1">
                                <LoadingButton 
                                    imgSrc="/icon-edit.png" 
                                    text="Edit"
                                    bgColor="white"
                                    onClick={(e) => onLessonEdit(source, lessonIndex, e)}
                                />
                                <LoadingButton 
                                    imgSrc="/icon-trash.png" 
                                    text="Delete"
                                    bgColor="white"
                                    onClick={(e) => onLessonDelete(source, lessonIndex, e)}
                                />
                            </div>
                        )}
                        {isReorderMode && (
                            <div 
                                {...provided.dragHandleProps}
                                className="absolute top-2 right-0 cursor-grab"
                            >
                                <span className="text-2xl text-gray-500 px-2">≡</span>
                            </div>
                        )}
                    </div>
                )}
            </Draggable>
        );
    };

    return <div className='text-left mx-auto max-w-screen-lg'>
        <h1 className="text-2xl">
            {lessonPlan ? "Edit study plan" : "Add study plan"}
        </h1>

        {
            !lessonPlan && <div>
                <div>
                    A study plan is a personalized learning plan designed to guide your child through their learning goals.
                </div>
                <div>
                    Create a new study plan by entering the following information:
                </div>
            </div>
        }

        <table className="table-auto w-full mt-2">
            <colgroup>
                <col />
                <col className="w-full" />
            </colgroup>
            <tbody>
                <tr>
                    <td className="text-right p-1">Subject</td>
                    <td className="text-left px-1">
                        <div className="flex flex-row items-center gap-2 py-1">
                            <input
                                type="text"
                                className="w-full border border-gray-300 p-1"
                                name="subject"
                                placeholder="eg, Math, Science, English, etc."
                                value={plan.subject}
                                maxLength={30}
                                onChange={handleChange} />
                            <span className="ml-2 hidden sm:block">Emoji</span>
                            <input type="text"
                                className="border border-gray-300 w-20 p-1 hidden sm:block"
                                name="emoji"
                                maxLength={2}
                                value={plan.emoji}
                                onChange={handleChange} />
                        </div>
                    </td>
                </tr>
                <tr className="sm:hidden">
                    <td className="text-right align-top p-1">Emoji</td>
                    <td className="px-1">
                        <input type="text"
                            className="border border-gray-300 w-20 p-1"
                            name="emoji"
                            maxLength={2}
                            value={plan.emoji}
                            onChange={handleChange} />
                    </td>
                </tr>
                <tr>
                    <td className="text-right align-top p-1">Learning goals</td>
                    <td className="px-1">
                        <textarea
                            className="border border-gray-300 w-full p-1 h-[5rem] sm:h-[4rem]"
                            name="goal"
                            placeholder="eg, Master 6th grade math concepts, develop reading comprehension skills and build vocabulary, etc."
                            value={plan.goal}
                            maxLength={120}
                            onChange={handleChange} />
                    </td>
                </tr>
                <tr>
                    <td className="text-right align-top p-1 md:whitespace-nowrap">Previous knowledge</td>
                    <td className="px-1">
                        <textarea
                            className="border border-gray-300 w-full p-1 h-[5rem] sm:h-[4rem]"
                            name="previous_knowledge"
                            placeholder="eg, Fully understands 5th grade math, can read at grade level but struggles with comprehension of more complex texts, etc."
                            value={plan.previous_knowledge}
                            maxLength={120}
                            onChange={handleChange} />
                    </td>
                </tr>
            </tbody>
        </table>

        <div className="flex flex-row w-full justify-center items-center gap-2 mt-2">
            <div className="flex gap-2">
                <LoadingButton 
                    text="Save"
                    disabled={!isSaveEnabled}
                    onClick={() => onOk(buildStudyPlanPayload(plan, lessons1))}
                />
                <button className="bg-blue-500 text-white py-2 px-4 rounded"
                    onClick={onCancel}>
                    Cancel
                </button>
            </div>
        </div>

        <div className="text-sm text-gray-600 mt-6">
            Use the + button below to add a new lesson. 
        </div>
        
        <AddNewLesson 
            isDialogOpen={isAddLessonDialogOpen}
            setIsDialogOpen={setIsAddLessonDialogOpen}
            onAddLesson={handleAddNewLesson}
        />
        <EditLesson
            lesson={getSelectedLesson()}
            isOpen={isEditLessonDialogOpen}
            onClose={() => setIsEditLessonDialogOpen(false)}
            onSave={handleEditLesson}
        />
        <EditTopic
            topic={getSelectedTopic()}
            isOpen={isEditTopicDialogOpen}
            onClose={() => setIsEditTopicDialogOpen(false)}
            onSave={handleEditTopic}
        />
        
        {
            lessons1.length > 0 && <div className="mt-6">
                <div className="flex flex-col md:flex-row gap-4 text-left mt-2">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-lg mb-2">
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-1">
                                    <div>Current study plan</div>
                                    <LoadingButton 
                                        imgSrc="/icon-plus.png" 
                                        text="Add lesson" 
                                        onClick={handleAddLesson}
                                    />
                                </div>
                                <LoadingButton 
                                    imgSrc="/icon-list-reorder3.png" 
                                    text={isReorderMode ? "Done Reordering" : "Reorder"}
                                    bgColor={isReorderMode ? 'white' : 'transparent'}
                                    onClick={toggleReorderMode}
                                />
                            </div>
                        </h1>
                        <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
                            <div className="flex-1">
                                <Droppable droppableId="lesson1" type="lessons">
                                    {(provided) => (
                                        <div ref={provided.innerRef} {...provided.droppableProps}>
                                            {
                                                lessons1.map((lesson, lessonIndex) => 
                                                    renderLesson('lesson1', lesson, lessonIndex, lessons1)
                                                )
                                            }
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </div>
                        </DragDropContext>
                    </div>

                    <div className="pt-4 md:w-60 lg:w-80 md:flex-shrink-0">
                        <StudyPlanFeedback
                            contentType="study plan"
                            feedback={feedback}
                            maxLength={400}
                            placeholder="Make lessons easier and reorganize topics so foundational ideas come first."
                            isRegenerating={isRegeneratingFromFeedback}
                            onFeedbackChange={setFeedback}
                            onSubmit={onRegenerateFromFeedback}
                            className="mt-4"
                        />
                        {
                            feedbackWarnings.length > 0 && (
                                <div className="mt-2 text-sm text-yellow-700">
                                    {`Revision warnings: ${feedbackWarnings.join('; ')}`}
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        }

    </div>
};

export default EditStudyPlan;
